import React, { type FC, useState, useCallback, useRef, useMemo } from 'react';
import { injectIntl, type WrappedComponentProps } from 'react-intl-next';

import { Box } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers';

import { fg } from '@confluence/feature-gating';
import {
	useApprovalsDispatch,
	type WorkflowStatus,
} from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { ContentState } from '../types.ts';
import type { ContentStateStateContainer } from '../ContentStateStateContainer.tsx';

import { ContentStateMenuV2SelectView } from './ContentStateMenuV2SelectView.tsx';
import { ContentStateMenuV2CreateView } from './ContentStateMenuV2CreateView.tsx';

export type DefaultSectionItem =
	| { type: 'contentState'; contentState: ContentState }
	| { type: 'workflowStatus'; workflowStatus: WorkflowStatus };

enum ConstentStateMenuViewTypes {
	SELECT = 'select',
	CREATE = 'create',
}

export type ContentStateMenuV2Props = WrappedComponentProps & {
	contentId: string;
	contentStateState: ContentStateStateContainer;
	contentType: 'page' | 'blogpost' | null;
	isLivePage: boolean;
	defaultStatesArray: (ContentState | null)[];
	customStatesArray: (ContentState | null)[];
	isContentStateEnabled: boolean;
	userOffline: boolean;
	onSelectExistingStatus: (selectedStatus: ContentState) => void;
	onSaveCustomStatus: (statusName: string, colorId: string) => void;
	onRemove: () => void;
	onBack: () => void;
	isContentStateMenuOpen: boolean;
	setIsContentStateMenuOpen: (newValue: boolean) => void;
	workflowStatuses: WorkflowStatus[];
	activeWorkflowStatus: WorkflowStatus | null;
	onSelectWorkflowStatus: (status: WorkflowStatus) => void;
};

const ContentStateMenuV2Component: FC<ContentStateMenuV2Props> = ({
	contentStateState,
	contentType,
	isLivePage,
	defaultStatesArray,
	customStatesArray,
	onSelectExistingStatus,
	onSaveCustomStatus,
	onRemove,
	onBack,
	setIsContentStateMenuOpen,
	workflowStatuses = [],
	activeWorkflowStatus,
	onSelectWorkflowStatus,
}) => {
	const { edition } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;
	const { resolveApprovalAgent } = useApprovalsDispatch();

	const [searchValue, setSearchValue] = useState<string>('');
	const [currentView, setCurrentView] = useState<ConstentStateMenuViewTypes>(
		ConstentStateMenuViewTypes.SELECT,
	);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const publish = usePublish('ai-mate');

	const handleInvokeRovo = useCallback(() => {
		void resolveApprovalAgent().then((agentId) => {
			if (!agentId) {
				return;
			}
			publish({
				type: 'chat-new',
				source: 'confluence-content-state',
				data: {
					name: 'Approve page',
					dialogues: [],
					prompt:
						'Use the "Approve page" skill to analyze this page and provide an approval decision.',
					agentId,
				},
			});
		});
		setIsContentStateMenuOpen(false);
	}, [publish, setIsContentStateMenuOpen, resolveApprovalAgent]);

	const validDefaultStates = useMemo(
		() => (defaultStatesArray || []).filter((s): s is ContentState => s !== null),
		[defaultStatesArray],
	);

	const validCustomStates = useMemo(
		() => (customStatesArray || []).filter((s): s is ContentState => s !== null),
		[customStatesArray],
	);

	const mergedDefaultSection = useMemo((): DefaultSectionItem[] => {
		const contentStateItems: DefaultSectionItem[] = validDefaultStates.map((s) => ({
			type: 'contentState' as const,
			contentState: s,
		}));

		if (contentType !== 'page' || isLivePage || workflowStatuses.length === 0 || !isPremium)
			return contentStateItems;

		const workflowItems: DefaultSectionItem[] = workflowStatuses.map((s) => ({
			type: 'workflowStatus' as const,
			workflowStatus: s,
		}));

		const restrictedIndex = contentStateItems.findIndex(
			(item) => item.type === 'contentState' && item.contentState.restrictionLevel !== 'NONE',
		);

		if (restrictedIndex !== -1) {
			const result = [...contentStateItems];
			result.splice(restrictedIndex, 0, ...workflowItems);
			return result;
		}
		return [...contentStateItems, ...workflowItems];
	}, [contentType, isLivePage, isPremium, validDefaultStates, workflowStatuses]);

	const handleSelectStatus = useCallback(
		(selectedStatus: ContentState) => {
			onSelectExistingStatus(selectedStatus);
			setIsContentStateMenuOpen(false);
			setSearchValue('');
		},
		[onSelectExistingStatus, setIsContentStateMenuOpen],
	);

	const handleRemove = useCallback(() => {
		onRemove();
		setIsContentStateMenuOpen(false);
	}, [onRemove, setIsContentStateMenuOpen]);

	const filteredDefaultSection = useMemo((): DefaultSectionItem[] => {
		if (!searchValue.trim()) return mergedDefaultSection;
		const lowerQuery = searchValue.toLowerCase();
		return mergedDefaultSection.filter((item) => {
			const name = item.type === 'contentState' ? item.contentState.name : item.workflowStatus.name;
			return name?.toLowerCase().includes(lowerQuery);
		});
	}, [mergedDefaultSection, searchValue]);

	const filteredCustomOptions = useMemo(() => {
		if (!searchValue.trim()) return validCustomStates;
		const lowerQuery = searchValue.toLowerCase();
		return validCustomStates.filter((option) => option.name?.toLowerCase().includes(lowerQuery));
	}, [validCustomStates, searchValue]);

	const hasResults = filteredDefaultSection.length > 0 || filteredCustomOptions.length > 0;

	const handleAddCustomClick = useCallback(() => {
		setCurrentView(ConstentStateMenuViewTypes.CREATE);
	}, []);

	const handleBackToSearch = useCallback(() => {
		setCurrentView(ConstentStateMenuViewTypes.SELECT);
	}, []);

	const handleSaveCustomStatus = useCallback(
		(statusName: string, colorId: string) => {
			onSaveCustomStatus(statusName, colorId);
			setIsContentStateMenuOpen(false);
			setCurrentView(ConstentStateMenuViewTypes.SELECT);
			setSearchValue('');
		},
		[onSaveCustomStatus, setIsContentStateMenuOpen],
	);

	const hasCurrentStatus =
		contentStateState.state.contentState !== null || activeWorkflowStatus !== null;

	if (currentView === ConstentStateMenuViewTypes.CREATE) {
		return (
			<Box ref={menuRef}>
				<ContentStateMenuV2CreateView
					initialValue={searchValue}
					onBack={handleBackToSearch}
					onSave={handleSaveCustomStatus}
				/>
			</Box>
		);
	}

	return (
		<Box ref={menuRef} testId="content-state-menu-v2">
			<ContentStateMenuV2SelectView
				searchValue={searchValue}
				onSearchChange={setSearchValue}
				filteredDefaultSection={filteredDefaultSection}
				filteredCustomOptions={filteredCustomOptions}
				hasResults={hasResults}
				hasCurrentStatus={hasCurrentStatus}
				onSelectStatus={handleSelectStatus}
				onSelectWorkflowStatus={onSelectWorkflowStatus}
				onAddCustomClick={handleAddCustomClick}
				onRemove={handleRemove}
				onBack={onBack}
				onInvokeRovo={fg('confluence_agentic_approvals') ? handleInvokeRovo : null}
			/>
		</Box>
	);
};

export const ContentStateMenuV2 = injectIntl(ContentStateMenuV2Component);
